import { useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { DataTable } from '@/components/ui/data-table';
import { Skeleton } from '@/components/ui/skeleton';
import { useToast } from '@/hooks/useToast';
import { useRecalls, useActiveRecalls, useRecallStats, useCreateRecall, useUpdateRecall, useRecordRecallAction } from '@/hooks/useRecalls';
import { useItems } from '@/hooks/useItems';
import { ModalForm, type FormField } from '@/components/ui/modal-form';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import {
  AlertTriangle,
  Plus,
  Search,
  ShieldAlert,
  Clock,
  CheckCircle,
  XCircle,
  Package,
} from 'lucide-react';
import { format } from 'date-fns';

const severityColors: Record<string, string> = {
  critical: 'bg-red-100 text-red-800 border-red-300',
  major: 'bg-orange-100 text-orange-800 border-orange-300',
  minor: 'bg-yellow-100 text-yellow-800 border-yellow-300',
};

const statusColors: Record<string, string> = {
  active: 'bg-red-100 text-red-800',
  in_progress: 'bg-orange-100 text-orange-800',
  completed: 'bg-green-100 text-green-800',
  cancelled: 'bg-gray-100 text-gray-800',
};

const actionStatusColors: Record<string, string> = {
  identified: 'bg-gray-100 text-gray-800',
  quarantined: 'bg-yellow-100 text-yellow-800',
  returned: 'bg-blue-100 text-blue-800',
  destroyed: 'bg-red-100 text-red-800',
  notified: 'bg-purple-100 text-purple-800',
  resolved: 'bg-green-100 text-green-800',
};

export default function RecallsPage() {
  const { toast } = useToast();
  const [searchQuery, setSearchQuery] = useState('');
  const [severityFilter, setSeverityFilter] = useState<string>('');
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [isDetailModalOpen, setIsDetailModalOpen] = useState(false);
  const [selectedRecall, setSelectedRecall] = useState<any>(null);
  const [isActionModalOpen, setIsActionModalOpen] = useState(false);
  const [selectedRecallItem, setSelectedRecallItem] = useState<any>(null);

  const { data: recalls = [], isLoading } = useRecalls({
    severity: severityFilter || undefined,
  });
  const { data: activeRecalls = [] } = useActiveRecalls();
  const { data: stats } = useRecallStats();
  const { data: itemsData } = useItems({ limit: 100 });

  const createMutation = useCreateRecall();
  const updateMutation = useUpdateRecall();
  const actionMutation = useRecordRecallAction();

  const createFields: FormField[] = [
    { name: 'recall_title', label: 'Recall Title', type: 'text', required: true },
    { name: 'description', label: 'Description', type: 'textarea' },
    { name: 'severity', label: 'Severity', type: 'select', required: true,
      options: [
        { value: 'critical', label: 'Critical' },
        { value: 'major', label: 'Major' },
        { value: 'minor', label: 'Minor' },
      ]},
    { name: 'source_type', label: 'Source', type: 'select',
      options: [
        { value: '', label: 'Select...' },
        { value: 'manufacturer', label: 'Manufacturer' },
        { value: 'regulatory', label: 'Regulatory' },
        { value: 'voluntary', label: 'Voluntary' },
        { value: 'internal', label: 'Internal' },
      ]},
    { name: 'source_reference', label: 'Reference Number', type: 'text' },
    { name: 'issue_date', label: 'Issue Date', type: 'date', required: true },
    { name: 'expected_completion_date', label: 'Expected Completion', type: 'date' },
    { name: 'affected_batches', label: 'Affected Batch Numbers', type: 'textarea', 
      description: 'Comma-separated batch numbers' },
    { name: 'internal_notes', label: 'Internal Notes', type: 'textarea' },
  ];

  const handleCreate = async (data: any) => {
    try {
      // Parse affected_batches from comma-separated to array
      const processedData = {
        ...data,
        affected_batches: data.affected_batches 
          ? data.affected_batches.split(',').map((b: string) => b.trim()).filter(Boolean)
          : [],
      };
      await createMutation.mutateAsync(processedData);
      toast({ title: 'Recall created successfully' });
      setIsCreateModalOpen(false);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const handleAction = async (actionType: string, quantity?: number) => {
    if (!selectedRecallItem || !selectedRecall) return;
    try {
      await actionMutation.mutateAsync({
        recallId: selectedRecall.recall_id,
        data: {
          recall_item_id: selectedRecallItem.recall_item_id,
          action_type: actionType,
          quantity,
        },
      });
      toast({ title: 'Action recorded successfully' });
      setIsActionModalOpen(false);
      setSelectedRecallItem(null);
    } catch (error: any) {
      toast({ title: 'Error', description: error.message, variant: 'destructive' });
    }
  };

  const columns = [
    {
      accessorKey: 'recall_number',
      header: 'Recall #',
      cell: ({ row }: any) => (
        <span className="font-mono text-sm">{row.recall_number}</span>
      ),
    },
    { accessorKey: 'recall_title', header: 'Title' },
    {
      accessorKey: 'severity',
      header: 'Severity',
      cell: ({ row }: any) => (
        <Badge className={severityColors[row.severity] + ' border'}>
          {row.severity}
        </Badge>
      ),
    },
    {
      accessorKey: 'status',
      header: 'Status',
      cell: ({ row }: any) => (
        <Badge className={statusColors[row.status] || 'bg-gray-100'}>
          {row.status.replace('_', ' ')}
        </Badge>
      ),
    },
    {
      accessorKey: 'issue_date',
      header: 'Issue Date',
      cell: ({ row }: any) => format(new Date(row.issue_date), 'dd MMM yyyy'),
    },
    {
      id: 'days_open',
      header: 'Days Open',
      cell: ({ row }: any) => {
        const days = Math.floor((Date.now() - new Date(row.issue_date).getTime()) / (1000 * 60 * 60 * 24));
        return days;
      },
    },
  ];

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold">Product Recalls</h1>
          <p className="text-muted-foreground">Manage product recalls and affected inventory</p>
        </div>
        <Button onClick={() => setIsCreateModalOpen(true)}>
          <Plus className="h-4 w-4 mr-2" />
          New Recall
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className={severityColors.critical + ' border-2'}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <ShieldAlert className="h-8 w-8" />
              <div>
                <p className="text-sm opacity-80">Critical</p>
                <p className="text-2xl font-bold">{stats?.active_by_severity?.critical || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={severityColors.major + ' border-2'}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <AlertTriangle className="h-8 w-8" />
              <div>
                <p className="text-sm opacity-80">Major</p>
                <p className="text-2xl font-bold">{stats?.active_by_severity?.major || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className={severityColors.minor + ' border-2'}>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Clock className="h-8 w-8" />
              <div>
                <p className="text-sm opacity-80">Minor</p>
                <p className="text-2xl font-bold">{stats?.active_by_severity?.minor || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <Package className="h-8 w-8 text-blue-600" />
              <div>
                <p className="text-sm text-muted-foreground">Affected Qty</p>
                <p className="text-2xl font-bold">{stats?.total_affected_qty || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="pt-6">
            <div className="flex items-center gap-4">
              <XCircle className="h-8 w-8 text-red-600" />
              <div>
                <p className="text-sm text-muted-foreground">Destroyed</p>
                <p className="text-2xl font-bold">{stats?.total_destroyed || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Active Recalls */}
      {activeRecalls.length > 0 && (
        <Card className="border-red-200 bg-red-50">
          <CardHeader>
            <CardTitle className="text-red-800 flex items-center gap-2">
              <ShieldAlert className="h-5 w-5" />
              Active Recalls ({activeRecalls.length})
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              {activeRecalls.map((recall: any) => (
                <div key={recall.recall_id} className="flex items-center justify-between p-3 bg-white rounded-lg border">
                  <div className="flex items-center gap-4">
                    <Badge className={severityColors[recall.severity]}>
                      {recall.severity}
                    </Badge>
                    <div>
                      <p className="font-medium">{recall.recall_title}</p>
                      <p className="text-sm text-muted-foreground">
                        {recall.recall_number} • {recall.days_open} days open
                      </p>
                    </div>
                  </div>
                  <Button variant="outline" size="sm">View Details</Button>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Filters */}
      <Card>
        <CardContent className="pt-6">
          <div className="flex flex-col md:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <input
                type="text"
                placeholder="Search recalls..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border rounded-lg"
              />
            </div>
            <select
              value={severityFilter}
              onChange={(e) => setSeverityFilter(e.target.value)}
              className="px-4 py-2 border rounded-lg"
            >
              <option value="">All Severity</option>
              <option value="critical">Critical</option>
              <option value="major">Major</option>
              <option value="minor">Minor</option>
            </select>
          </div>
        </CardContent>
      </Card>

      {/* Data Table */}
      <Card>
        <CardContent className="pt-6">
          {isLoading ? (
            <div className="space-y-4">
              {[1, 2, 3, 4, 5].map((i) => (
                <Skeleton key={i} className="h-12 w-full" />
              ))}
            </div>
          ) : (
            <DataTable columns={columns} data={recalls} />
          )}
        </CardContent>
      </Card>

      {/* Create Modal */}
      <ModalForm
        isOpen={isCreateModalOpen}
        onClose={() => setIsCreateModalOpen(false)}
        onSubmit={handleCreate}
        fields={createFields}
        title="Create Product Recall"
        isLoading={createMutation.isPending}
      />
    </div>
  );
}
